// SWR mutations for API operations that modify data
import { useState } from 'react';
import { apiService, OrderCreateDto, PaymentRequest, CreateConversationRequest } from '../services/api';
import { mutate } from 'swr';

// 通用mutation hook
export const useApiMutation = <T, P = any>(
  mutationFn: (params: P) => Promise<T>,
  options?: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
    invalidateKeys?: string[];
  }
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = async (params: P): Promise<T | undefined> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await mutationFn(params);
      options?.onSuccess?.(result);
      
      // 使相关缓存失效
      if (options?.invalidateKeys) {
        options.invalidateKeys.forEach(key => {
          mutate(key as any);
        });
      }
      
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('操作失败');
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    mutate,
    isLoading,
    error,
  };
};

// 订单相关mutations
export const useCreateOrder = () => {
  return useApiMutation(
    (orderData: OrderCreateDto) => apiService.createOrder(orderData),
    {
      onSuccess: () => {
        // 创建订单后刷新订单列表
        mutate('/api/orders/user');
      },
      invalidateKeys: ['/api/orders/user'],
    }
  );
};

export const useUpdateOrderStatus = () => {
  return useApiMutation(
    ({ orderId, status, notes }: { orderId: number; status: string; notes?: string }) =>
      apiService.updateOrderStatus(orderId, status, notes),
    {
      onSuccess: () => {
        // 更新订单状态后刷新相关数据
        mutate('/api/orders/user');
      },
      invalidateKeys: ['/api/orders/user'],
    }
  );
};

export const useCancelOrder = () => {
  return useApiMutation(
    ({ orderId, reason }: { orderId: number; reason?: string }) =>
      apiService.cancelOrder(orderId, reason),
    {
      onSuccess: () => {
        // 取消订单后刷新订单列表
        mutate('/api/orders/user');
      },
      invalidateKeys: ['/api/orders/user'],
    }
  );
};

// 支付相关mutations
export const useCreatePayment = () => {
  return useApiMutation(
    (paymentData: PaymentRequest) => apiService.createPayment(paymentData),
    {
      onSuccess: () => {
        // 创建支付后刷新支付状态
        mutate('/api/payments/order');
      },
      invalidateKeys: ['/api/payments/order'],
    }
  );
};

// 客服相关mutations
export const useCreateConversation = () => {
  return useApiMutation(
    (request: CreateConversationRequest) => apiService.createConversation(request),
    {
      onSuccess: () => {
        // 创建对话后刷新对话列表
        mutate('/api/support/users');
      },
      invalidateKeys: ['/api/support/users'],
    }
  );
};

export const useSendMessage = () => {
  return useApiMutation(
    ({ conversationId, senderId, senderType, message }: {
      conversationId: number;
      senderId: number;
      senderType: 'USER' | 'ADMIN';
      message: string;
    }) => apiService.sendMessage(conversationId, senderId, senderType, message),
    {
      onSuccess: () => {
        // 发送消息后刷新消息列表
        mutate('/api/support/conversations');
      },
      invalidateKeys: ['/api/support/conversations'],
    }
  );
};

// 通用数据刷新hook
export const useRefreshData = () => {
  const refreshAll = () => {
    // 刷新所有相关数据
    mutate('/api/products');
    mutate('/api/orders/user');
    mutate('/api/deliveries/user');
    mutate('/api/support/users');
  };

  const refreshProducts = () => {
    mutate('/api/products');
  };

  const refreshOrders = () => {
    mutate('/api/orders/user');
  };

  const refreshDeliveries = () => {
    mutate('/api/deliveries/user');
  };

  const refreshSupport = () => {
    mutate('/api/support/users');
  };

  return {
    refreshAll,
    refreshProducts,
    refreshOrders,
    refreshDeliveries,
    refreshSupport,
  };
};
